/**
 * Règles de suffixes du français publiées par Snowball (Martin Porter).
 * Elles servent seulement à comparer deux mots. La forme raccourcie
 * n'est jamais montrée à l'utilisateur.
 */

const VOWELS = new Set("aeiouyâàëéêèïîôûù");
const MIN_WORD_LENGTH = 4;
const MIN_STEM_LENGTH = 4;
const MIN_FAMILY_ROOT = 4;
const FAMILY_ENDINGS = ["arde", "ard", "ar"];

function isVowel(ch) {
  return VOWELS.has(ch);
}

function indexAfter(word, from, predicate) {
  for (let i = from; i < word.length; i += 1) {
    if (predicate(word[i])) return i + 1;
  }
  return -1;
}

function elisions(word) {
  const match = word.match(/^(?:qu|[cdjlmnstz])'(.+)$/);
  return match ? match[1] : word;
}

function prelude(word) {
  const chars = [...word];
  let i = 0;
  while (i < chars.length) {
    const a = chars[i];
    const b = chars[i + 1];
    const c = chars[i + 2];
    if (isVowel(a) && b === "u" && isVowel(c)) {
      chars[i + 1] = "U";
      i += 3;
      continue;
    }
    if (isVowel(a) && b === "i" && isVowel(c)) {
      chars[i + 1] = "I";
      i += 3;
      continue;
    }
    if (isVowel(a) && b === "y") {
      chars[i + 1] = "Y";
      i += 2;
      continue;
    }
    if (a === "ë") {
      chars.splice(i, 1, "H", "e");
      i += 2;
      continue;
    }
    if (a === "ï") {
      chars.splice(i, 1, "H", "i");
      i += 2;
      continue;
    }
    if (a === "y" && isVowel(b)) {
      chars[i] = "Y";
      i += 2;
      continue;
    }
    if (a === "q" && b === "u") {
      chars[i + 1] = "U";
      i += 2;
      continue;
    }
    i += 1;
  }
  return chars.join("");
}

function markRegions(word) {
  const n = word.length;
  let pV = n;
  let p1 = n;
  let p2 = n;

  if (n >= 3 && isVowel(word[0]) && isVowel(word[1])) {
    pV = 3;
  } else if (word.startsWith("par") || word.startsWith("col") || word.startsWith("tap")) {
    pV = 3;
  } else if (n >= 3 && word.startsWith("ni") && isVowel(word[2])) {
    pV = 3;
  } else {
    const after = indexAfter(word, 1, isVowel);
    if (after !== -1) pV = after;
  }

  const afterVowel = indexAfter(word, 0, isVowel);
  if (afterVowel !== -1) {
    const afterNon = indexAfter(word, afterVowel, (ch) => !isVowel(ch));
    if (afterNon !== -1) {
      p1 = afterNon;
      const afterVowel2 = indexAfter(word, p1, isVowel);
      if (afterVowel2 !== -1) {
        const afterNon2 = indexAfter(word, afterVowel2, (ch) => !isVowel(ch));
        if (afterNon2 !== -1) p2 = afterNon2;
      }
    }
  }

  return { pV, p1, p2 };
}

function longestIn(word, suffixes, minStart) {
  let best = "";
  for (const suffix of suffixes) {
    if (suffix.length <= best.length) continue;
    if (!word.endsWith(suffix)) continue;
    if (word.length - suffix.length < minStart) continue;
    best = suffix;
  }
  return best;
}

function cut(word, suffix, replacement = "") {
  return word.slice(0, word.length - suffix.length) + replacement;
}

const R2_DELETE = [
  "atrices",
  "ateurs",
  "ations",
  "ances",
  "iqUes",
  "ismes",
  "ables",
  "istes",
  "atrice",
  "ateur",
  "ation",
  "ance",
  "iqUe",
  "isme",
  "able",
  "iste",
  "eux",
];

const ATION_LIKE = ["atrices", "ateurs", "ations", "atrice", "ateur", "ation"];

function tryIc(word, p2) {
  if (!word.endsWith("ic")) return word;
  const start = word.length - 2;
  if (start >= p2) return word.slice(0, -2);
  return `${word.slice(0, -2)}iqU`;
}

function tryEmentTail(word, regions) {
  const { pV, p1, p2 } = regions;
  const options = ["iqU", "ièr", "Ièr", "eus", "abl", "iv"];
  const suffix = longestIn(word, options, 0);
  if (!suffix) return word;
  const start = word.length - suffix.length;
  if (suffix === "iv") {
    if (start < p2) return word;
    let next = word.slice(0, start);
    if (next.endsWith("at") && next.length - 2 >= p2) next = next.slice(0, -2);
    return next;
  }
  if (suffix === "eus") {
    if (start >= p2) return word.slice(0, start);
    if (start >= p1) return `${word.slice(0, start)}eux`;
    return word;
  }
  if (suffix === "abl" || suffix === "iqU") {
    if (start < p2) return word;
    return word.slice(0, start);
  }
  if (start < pV) return word;
  return `${word.slice(0, start)}i`;
}

function tryIteTail(word, p2) {
  const suffix = longestIn(word, ["abil", "ic", "iv"], 0);
  if (!suffix) return word;
  const start = word.length - suffix.length;
  if (suffix === "abil") {
    if (start >= p2) return word.slice(0, start);
    return `${word.slice(0, start)}abl`;
  }
  if (suffix === "ic") return tryIc(word, p2);
  if (start >= p2) return word.slice(0, start);
  return word;
}

function tryIveTail(word, p2) {
  if (!word.endsWith("at") || word.length - 2 < p2) return word;
  const withoutAt = word.slice(0, -2);
  return tryIc(withoutAt, p2);
}

function standardSuffix(word, regions) {
  const { pV, p1, p2 } = regions;
  const rules = [
    ...R2_DELETE,
    "logies",
    "logie",
    "usions",
    "utions",
    "usion",
    "ution",
    "ences",
    "ence",
    "ements",
    "ement",
    "ités",
    "ité",
    "ives",
    "ifs",
    "ive",
    "if",
    "eaux",
    "aux",
    "oux",
    "euses",
    "euse",
    "issements",
    "issement",
    "amment",
    "emment",
    "ments",
    "ment",
  ];
  const suffix = longestIn(word, rules, 0);
  if (!suffix) return { word, applied: false, forceVerb: false };
  const start = word.length - suffix.length;

  if (R2_DELETE.includes(suffix)) {
    if (start < p2) return { word, applied: false, forceVerb: false };
    let next = cut(word, suffix);
    if (ATION_LIKE.includes(suffix)) next = tryIc(next, p2);
    return { word: next, applied: true, forceVerb: false };
  }
  if (suffix === "logie" || suffix === "logies") {
    if (start < p2) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "log"), applied: true, forceVerb: false };
  }
  if (suffix === "usion" || suffix === "ution" || suffix === "usions" || suffix === "utions") {
    if (start < p2) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "u"), applied: true, forceVerb: false };
  }
  if (suffix === "ence" || suffix === "ences") {
    if (start < p2) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "ent"), applied: true, forceVerb: false };
  }
  if (suffix === "ement" || suffix === "ements") {
    if (start < pV) return { word, applied: false, forceVerb: false };
    const next = tryEmentTail(cut(word, suffix), regions);
    return { word: next, applied: true, forceVerb: false };
  }
  if (suffix === "ité" || suffix === "ités") {
    if (start < p2) return { word, applied: false, forceVerb: false };
    return { word: tryIteTail(cut(word, suffix), p2), applied: true, forceVerb: false };
  }
  if (suffix === "if" || suffix === "ive" || suffix === "ifs" || suffix === "ives") {
    if (start < p2) return { word, applied: false, forceVerb: false };
    return { word: tryIveTail(cut(word, suffix), p2), applied: true, forceVerb: false };
  }
  if (suffix === "eaux") {
    return { word: cut(word, suffix, "eau"), applied: true, forceVerb: false };
  }
  if (suffix === "aux") {
    if (start < p1) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "al"), applied: true, forceVerb: false };
  }
  if (suffix === "oux") {
    const prev = word[start - 1];
    if (!"bhjlnp".includes(prev || "")) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "ou"), applied: true, forceVerb: false };
  }
  if (suffix === "euse" || suffix === "euses") {
    if (start >= p2) return { word: cut(word, suffix), applied: true, forceVerb: false };
    if (start >= p1) return { word: cut(word, suffix, "eux"), applied: true, forceVerb: false };
    return { word, applied: false, forceVerb: false };
  }
  if (suffix === "issement" || suffix === "issements") {
    if (start < p1 || start < 1 || isVowel(word[start - 1])) {
      return { word, applied: false, forceVerb: false };
    }
    return { word: cut(word, suffix), applied: true, forceVerb: false };
  }
  if (suffix === "amment") {
    if (start < pV) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "ant"), applied: false, forceVerb: true };
  }
  if (suffix === "emment") {
    if (start < pV) return { word, applied: false, forceVerb: false };
    return { word: cut(word, suffix, "ent"), applied: false, forceVerb: true };
  }
  if (start < 1 || !isVowel(word[start - 1]) || start - 1 < pV) {
    return { word, applied: false, forceVerb: false };
  }
  return { word: cut(word, suffix), applied: false, forceVerb: true };
}

const I_VERB = [
  "issaIent",
  "issantes",
  "issants",
  "issante",
  "issions",
  "irions",
  "issais",
  "issait",
  "issant",
  "issent",
  "issiez",
  "issons",
  "isses",
  "iriez",
  "irons",
  "iront",
  "irais",
  "irait",
  "irent",
  "iraIent",
  "îmes",
  "îtes",
  "issez",
  "isse",
  "iras",
  "irez",
  "irai",
  "ies",
  "ira",
  "ît",
  "ir",
  "is",
  "it",
  "ie",
  "i",
];

function iVerbSuffix(word, regions) {
  const suffix = longestIn(word, I_VERB, regions.pV);
  if (!suffix) return { word, applied: false };
  const start = word.length - suffix.length;
  if (start < 1 || start - 1 < regions.pV) return { word, applied: false };
  const prev = word[start - 1];
  if (isVowel(prev) || prev === "H") return { word, applied: false };
  return { word: cut(word, suffix), applied: true };
}

const VERB_PLAIN = [
  "eraIent",
  "erions",
  "eriez",
  "èrent",
  "erons",
  "eront",
  "erais",
  "erait",
  "erai",
  "eras",
  "erez",
  "era",
  "ées",
  "ée",
  "és",
  "ez",
  "iez",
  "er",
  "é",
];

const VERB_A = [
  "assions",
  "assiez",
  "aIent",
  "antes",
  "assent",
  "asses",
  "âmes",
  "asse",
  "ants",
  "ante",
  "âtes",
  "ait",
  "ant",
  "ais",
  "ât",
  "as",
  "ai",
  "a",
];

function protectedAis(word, suffix) {
  const stem = word.slice(0, word.length - suffix.length);
  if (stem.endsWith("auv") || stem.endsWith("épl")) return true;
  if (!stem.endsWith("al")) return false;
  return stem.length === 3;
}

function verbSuffix(word, regions) {
  const { pV, p2 } = regions;
  const suffix = longestIn(word, ["ions", "eais", "aises", "aise", ...VERB_PLAIN, ...VERB_A], pV);
  if (!suffix) return { word, applied: false };
  const start = word.length - suffix.length;

  if (suffix === "ions") {
    if (start < p2) return { word, applied: false };
    return { word: cut(word, suffix), applied: true };
  }
  if (suffix === "ais" || suffix === "aise" || suffix === "aises") {
    if (protectedAis(word, suffix)) return { word, applied: false };
    return { word: cut(word, suffix), applied: true };
  }
  if (VERB_A.includes(suffix)) {
    let next = cut(word, suffix);
    if (next.endsWith("e") && next.length - 1 >= pV) next = next.slice(0, -1);
    return { word: next, applied: true };
  }
  return { word: cut(word, suffix), applied: true };
}

function step3(word) {
  if (word.endsWith("Y")) return `${word.slice(0, -1)}i`;
  if (word.endsWith("ç")) return `${word.slice(0, -1)}c`;
  return word;
}

function residualSuffix(word, regions) {
  const { pV, p2 } = regions;
  if (word.endsWith("s") && word.length >= 2) {
    const prev = word[word.length - 2];
    const keep = "aiouès".includes(prev);
    if (word.endsWith("His") || !keep) word = word.slice(0, -1);
  }
  const suffix = longestIn(word, ["ière", "Ière", "Ier", "ier", "ion", "e"], pV);
  if (!suffix) return word;
  const start = word.length - suffix.length;
  if (suffix === "ion") {
    if (start < p2 || start < 1) return word;
    const prev = word[start - 1];
    if (prev !== "s" && prev !== "t") return word;
    return word.slice(0, start);
  }
  if (suffix === "e") return word.slice(0, start);
  return `${word.slice(0, start)}i`;
}

function undouble(word) {
  if (word.endsWith("eill") || word.endsWith("enn") || word.endsWith("onn") || word.endsWith("ett") || word.endsWith("ell")) {
    return word.slice(0, -1);
  }
  return word;
}

function unaccent(word) {
  let i = word.length - 1;
  if (i < 0 || isVowel(word[i])) return word;
  while (i >= 0 && !isVowel(word[i])) i -= 1;
  if (i >= 0 && (word[i] === "é" || word[i] === "è")) {
    return `${word.slice(0, i)}e${word.slice(i + 1)}`;
  }
  return word;
}

function postlude(word) {
  let out = "";
  for (let i = 0; i < word.length; i += 1) {
    const two = word.slice(i, i + 2);
    if (two === "He") {
      out += "ë";
      i += 1;
      continue;
    }
    if (two === "Hi") {
      out += "ï";
      i += 1;
      continue;
    }
    const ch = word[i];
    if (ch === "I") out += "i";
    else if (ch === "U") out += "u";
    else if (ch === "Y") out += "y";
    else if (ch !== "H") out += ch;
  }
  return out;
}

export function frenchStem(raw) {
  let word = String(raw ?? "")
    .toLowerCase()
    .replace(/’/g, "'")
    .trim();
  if (!word) return "";
  word = elisions(word);
  word = prelude(word);
  const regions = markRegions(word);
  const step1 = standardSuffix(word, regions);
  let changed = word;
  let success = false;
  if (step1.applied && !step1.forceVerb) {
    changed = step1.word;
    success = true;
  } else {
    if (step1.forceVerb) changed = step1.word;
    const step2a = iVerbSuffix(changed, regions);
    if (step2a.applied) {
      changed = step2a.word;
      success = true;
    } else {
      const step2b = verbSuffix(changed, regions);
      if (step2b.applied) {
        changed = step2b.word;
        success = true;
      }
    }
  }
  changed = success ? step3(changed) : residualSuffix(changed, regions);
  changed = undouble(changed);
  changed = unaccent(changed);
  return postlude(changed);
}

function normalizeWord(raw) {
  return String(raw ?? "")
    .toLowerCase()
    .replace(/’/g, "'")
    .trim();
}

function singularize(word) {
  if (word.length < 5 || !word.endsWith("s")) return word;
  const prev = word[word.length - 2];
  if ("aiouès".includes(prev)) return word;
  return word.slice(0, -1);
}

function familyRoot(word) {
  for (const ending of FAMILY_ENDINGS) {
    if (!word.endsWith(ending)) continue;
    const root = word.slice(0, -ending.length);
    return [...root].length >= MIN_FAMILY_ROOT ? root : "";
  }
  return "";
}

function comparisonKeys(word) {
  const clean = normalizeWord(word);
  if ([...clean].length < MIN_WORD_LENGTH || /[\s-]/.test(clean)) return new Set();
  const base = singularize(clean);
  const keys = new Set();
  const stem = frenchStem(base);
  if ([...stem].length >= MIN_STEM_LENGTH) keys.add(`s:${stem}`);
  const root = familyRoot(base);
  if (root) keys.add(`f:${root}`);
  return keys;
}

function sharesComparisonKey(left, right) {
  for (const key of left) {
    if (right.has(key)) return true;
  }
  return false;
}

export function findCloseRegisterWords(word, registerWords) {
  const keys = comparisonKeys(word);
  if (keys.size === 0) return [];
  const self = normalizeWord(word);
  const found = new Set();
  for (const entry of Array.isArray(registerWords) ? registerWords : []) {
    const other = normalizeWord(entry);
    if (!other || other === self || found.has(other)) continue;
    if (sharesComparisonKey(keys, comparisonKeys(other))) found.add(other);
  }
  return [...found].sort((a, b) => a.localeCompare(b, "fr"));
}
